import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { useMutation, useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import Form from "src/components/Form"

import { getArchivedContactBoats, restoreContact } from "src/api/Contacts"

import { formatDimension } from "src/utils/UnitConversion"

const ArchivedContactRestorePanel = ({
  archivedContacts,
  onRestored,
  onCancel,
}) => {
  const { marinaSlug } = useMarina()
  const [selectedContactId, setSelectedContactId] = useState(
    archivedContacts.length === 1 ? archivedContacts[0].id : null
  )
  const [selectedBoatIds, setSelectedBoatIds] = useState(new Set())
  const [errorMessage, setErrorMessage] = useState(null)

  const selectedContact = useMemo(
    () => archivedContacts.find((c) => c.id === selectedContactId),
    [archivedContacts, selectedContactId]
  )

  const { data: boats = [], isFetching } = useQuery(
    ["archivedBoats", marinaSlug, selectedContactId],
    () => getArchivedContactBoats(marinaSlug, selectedContactId),
    { enabled: !!selectedContactId, refetchOnWindowFocus: false }
  )

  const toggleBoat = (id) => {
    setSelectedBoatIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedBoatIds(new Set(boats.map((b) => b.id)))
  const selectNone = () => setSelectedBoatIds(new Set())

  const { mutate: submitRestore, isLoading: isRestoring } = useMutation(
    () =>
      restoreContact(marinaSlug, selectedContactId, {
        contactBoatIds: Array.from(selectedBoatIds),
      }),
    {
      onSuccess: (data) =>
        onRestored({ contact: data.contact, contactBoats: data.contactBoats }),
      onError: (error) =>
        setErrorMessage(error?.error || "Failed to restore contact"),
    }
  )

  if (!selectedContact) {
    return (
      <div className="border-amber-200 bg-amber-50 rounded border p-4">
        <h4 className="font-medium">
          Multiple archived contacts with this email
        </h4>
        <p className="mt-1 text-sm text-gray-700">Select one to restore:</p>
        <ul className="mt-2 flex flex-col gap-1">
          {archivedContacts.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="text-blue-600 underline"
                onClick={() => setSelectedContactId(c.id)}
              >
                {`${c.name} — archived ${new Date(
                  c.deletedAt
                ).toLocaleDateString()}`}
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex justify-end">
          <Button variant="secondary" type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="border-amber-200 bg-amber-50 rounded border p-4">
      <p className="font-medium">
        {selectedContact.name} was archived{" "}
        {new Date(selectedContact.deletedAt).toLocaleDateString()}
      </p>

      {boats.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-700">
              {boats.length} archived {pluralize("boat", boats.length)} on file
            </p>
            <div className="flex gap-2 text-sm">
              <Button variant="link" onClick={selectAll}>
                Select all
              </Button>
              <Button variant="link" onClick={selectNone}>
                Select none
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {boats.map((boat) => (
              <Form.Checkbox
                type="checkbox"
                name={`archived-boat-${boat.id}`}
                checked={selectedBoatIds.has(boat.id)}
                onChange={() => toggleBoat(boat.id)}
                key={boat.id}
                label={
                  <span>
                    {boat.name}
                    {boat.year ? ` · ${boat.year}` : ""}
                    {boat.boatType ? ` · ${boat.boatType}` : ""}
                    {boat.lengthOverallInches
                      ? ` · ${formatDimension(boat.lengthOverallInches)}`
                      : ""}
                  </span>
                }
              />
            ))}
          </div>
        </div>
      )}

      {isFetching && (
        <p className="mt-2 text-sm text-gray-500">
          Loading archived boats&hellip;
        </p>
      )}
      {errorMessage && <Form.Error>{errorMessage}</Form.Error>}

      <div className="mt-3 flex justify-end gap-2">
        <Button
          variant="secondary"
          type="button"
          onClick={onCancel}
          disabled={isRestoring}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={submitRestore}
          isLoading={isRestoring}
        >
          {selectedBoatIds.size > 0
            ? "Restore customer and selected boats"
            : "Restore customer"}
        </Button>
      </div>
    </div>
  )
}

ArchivedContactRestorePanel.propTypes = {
  archivedContacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string,
      deletedAt: PropTypes.string,
    })
  ).isRequired,
  onRestored: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default ArchivedContactRestorePanel
